"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  CANADIAN_SCALES,
  type CanadianScale,
  type CanadianSummary,
  canadianBands,
  canadianLetters,
  convertCanadianMark,
  pointsForLetter,
  summariseCanadian,
} from "@/lib/tool-kit/intl-grades/canadian-gpa";

/** English strings. A translated content file overrides these through `ui`. */
export const CANADIAN_GPA_CONVERTER_STRINGS = {
  scaleLabel: "Conversion scale",
  scales: {
    uoft: "University of Toronto — the usual Ontario 4.0 table",
    "ubc-alberta": "UBC percentage bands with Alberta's 4-point letter values",
  },
  scaleHint:
    "Canada has no national scale. These are two published ones; the university or application service you apply to decides which applies.",
  inputLabel: "You have",
  inputs: {
    percent: "Percentage marks",
    letter: "Letter grades",
  },
  coursesHeading: "Your courses",
  markLabel: "Mark % for course {n}",
  letterLabel: "Letter grade for course {n}",
  creditsLabel: "Credits for course {n}",
  addCourse: "Add a course",
  removeCourse: "Remove",
  removeCourseAria: "Remove course {n}",
  results: "Result",
  gpa: "GPA (4.0 scale)",
  average: "Weighted average mark",
  averageLetter: "Letter for the average",
  creditsCounted: "Credits counted",
  tableHeading: "Course by course",
  colCourse: "Course",
  colMark: "Mark %",
  colLetter: "Letter",
  colPoints: "Grade points",
  colCredits: "Credits",
  courseName: "Course {n}",
  noMark: "—",
  skipped: "Skipped (not a usable grade, or no credits): {rows}",
  empty: "Enter at least one course with a grade and credits above zero.",
  bandsHeading: "The scale in use",
  colFrom: "Mark from",
  ubcNote:
    "UBC publishes percentage bands but no grade points, so this scale scores them with the four-point letter values the University of Alberta publishes.",
  caveat:
    "Professional and graduate application services, such as OMSAS for Ontario medical schools, re-convert grades with their own university-by-university tables, so their GPA can differ from this one.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "Canadian GPA {gpa} (4.0 scale)",
} as const;

type Strings = typeof CANADIAN_GPA_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type InputKind = keyof Strings["inputs"];

interface Row {
  id: number;
  mark: string;
  letter: string;
  credits: string;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, mark: "92", letter: "A+", credits: "3" },
  { id: 2, mark: "84", letter: "A−", credits: "3" },
  { id: 3, mark: "78", letter: "B+", credits: "3" },
  { id: 4, mark: "71", letter: "B−", credits: "3" },
  { id: 5, mark: "65", letter: "C", credits: "6" },
];

const INPUT_KINDS: readonly InputKind[] = ["percent", "letter"];
const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
const ONE_DECIMAL = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

function CourseRow({
  row,
  index,
  kind,
  letters,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  kind: InputKind;
  letters: readonly string[];
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
      {kind === "percent" ? (
        <TextField
          id={`cgc-mark-${row.id}`}
          label={t(strings.markLabel, { n })}
          numeric
          onChange={(mark) => onChange({ ...row, mark })}
          value={row.mark}
        />
      ) : (
        <SelectField
          id={`cgc-letter-${row.id}`}
          label={t(strings.letterLabel, { n })}
          onChange={(letter) => onChange({ ...row, letter })}
          options={letters.map((letter) => ({ value: letter, label: letter }))}
          value={row.letter}
        />
      )}
      <TextField
        id={`cgc-credits-${row.id}`}
        label={t(strings.creditsLabel, { n })}
        numeric
        onChange={(credits) => onChange({ ...row, credits })}
        value={row.credits}
      />
      <button
        aria-label={t(strings.removeCourseAria, { n })}
        className={BUTTON_SECONDARY}
        onClick={onRemove}
        type="button"
      >
        {strings.removeCourse}
      </button>
    </div>
  );
}

function ResultsSection({
  summary,
  tableRows,
  skipped,
  scale,
  strings,
  fmt,
}: {
  summary: CanadianSummary | null;
  tableRows: string[][];
  skipped: string[];
  scale: CanadianScale;
  strings: Strings;
  fmt: Format;
}) {
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {summary === null ? (
        <Notice>{strings.empty}</Notice>
      ) : (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.gpa}
              value={fmt(summary.gpa, TWO_DECIMALS)}
            />
            {summary.averageMark === null ? null : (
              <StatCard
                label={strings.average}
                value={fmt(summary.averageMark, { maximumFractionDigits: 2 })}
              />
            )}
            {summary.averageBand === null ? null : (
              <StatCard
                label={strings.averageLetter}
                value={summary.averageBand.letter}
              />
            )}
            <StatCard
              label={strings.creditsCounted}
              value={fmt(summary.totalCredits, { maximumFractionDigits: 2 })}
            />
          </StatGrid>
          {skipped.length > 0 ? (
            <p className="text-muted-foreground text-sm">
              {t(strings.skipped, { rows: skipped.join(", ") })}
            </p>
          ) : null}
          <DataTable
            caption={strings.tableHeading}
            head={[
              strings.colCourse,
              strings.colMark,
              strings.colLetter,
              strings.colPoints,
              strings.colCredits,
            ]}
            rows={tableRows}
          />
          <DataTable
            caption={strings.bandsHeading}
            head={[strings.colFrom, strings.colLetter, strings.colPoints]}
            rows={canadianBands(scale).map((band) => [
              fmt(band.min),
              band.letter,
              fmt(band.points, ONE_DECIMAL),
            ])}
          />
          {scale === "ubc-alberta" ? (
            <p className="text-muted-foreground text-sm">{strings.ubcNote}</p>
          ) : null}
          <p className="text-muted-foreground text-sm">{strings.caveat}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyResult}
            small
            text={t(strings.summary, { gpa: fmt(summary.gpa, TWO_DECIMALS) })}
          />
        </>
      )}
    </section>
  );
}

export default function CanadianGpaConverterWidget() {
  const strings = useToolUi(CANADIAN_GPA_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [scale, setScale] = useState<CanadianScale>("uoft");
  const [kind, setKind] = useState<InputKind>("percent");
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(6);

  const letters = canadianLetters(scale);

  function changeScale(next: CanadianScale) {
    setScale(next);
    const allowed = canadianLetters(next);
    setRows((current) =>
      current.map((row) =>
        allowed.includes(row.letter)
          ? row
          : { ...row, letter: allowed.at(-2) ?? row.letter }
      )
    );
  }

  const parsed = rows.map((row) => ({
    row,
    mark: kind === "percent" ? parseNumberInput(row.mark) : null,
    credits: parseNumberInput(row.credits),
  }));
  const usable = parsed.filter(
    ({ mark }) => kind === "letter" || mark !== null
  );
  const courses = usable.flatMap(({ row, mark, credits }) =>
    credits === null
      ? []
      : [{ mark, letter: kind === "letter" ? row.letter : null, credits }]
  );
  const summary = summariseCanadian(courses, scale);
  const tableRows = parsed.flatMap(({ row, mark, credits }, index) => {
    if (credits === null || credits <= 0) {
      return [];
    }
    const band = mark === null ? null : convertCanadianMark(mark, scale);
    const letter = band ? band.letter : row.letter;
    const points = band ? band.points : pointsForLetter(row.letter, scale);
    if ((kind === "percent" && band === null) || points === null) {
      return [];
    }
    return [
      [
        t(strings.courseName, { n: fmt(index + 1) }),
        mark === null
          ? strings.noMark
          : fmt(mark, { maximumFractionDigits: 2 }),
        letter,
        fmt(points, ONE_DECIMAL),
        fmt(credits, { maximumFractionDigits: 2 }),
      ],
    ];
  });
  const shown = new Set(tableRows.map((cells) => cells[0]));
  const skipped = parsed.flatMap((_, index) =>
    shown.has(t(strings.courseName, { n: fmt(index + 1) }))
      ? []
      : [fmt(index + 1)]
  );

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.scaleHint}
          id="cgc-scale"
          label={strings.scaleLabel}
          onChange={changeScale}
          options={CANADIAN_SCALES.map((value) => ({
            value,
            label: strings.scales[value],
          }))}
          value={scale}
        />
        <PillGroup
          label={strings.inputLabel}
          name="cgc-input"
          onChange={setKind}
          options={INPUT_KINDS.map((value) => ({
            value,
            label: strings.inputs[value],
          }))}
          value={kind}
        />
        <h3 className={WIDGET_H3}>{strings.coursesHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <CourseRow
              fmt={fmt}
              index={index}
              key={row.id}
              kind={kind}
              letters={letters}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, mark: "", letter: "B", credits: "3" },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addCourse}
        </button>
      </div>
      <ResultsSection
        fmt={fmt}
        scale={scale}
        skipped={skipped}
        strings={strings}
        summary={summary}
        tableRows={tableRows}
      />
    </div>
  );
}
